import { useState, useEffect, useRef } from "react";
import "./style.css";

const servicesHierarchy = {
  "Registration": {
    "Company Registration": [
      "Private Limited Company",
      "Limited Liability Partnership (LLP)",
      "One Person Company",
      "Section 8 Company (Non Profit Organization)",
      "Public Limited Company"
    ],
    "GST Registration": null,
    "Professional Tax Registration (P-Tax)": null,
    "Pan & Tan Registration": null,
    "Udyam / MSME Registration": null,
    "Startup India Registration": null,
    "Import Export Code": null
  },
  "Tax & Compliances": {
    "Direct Tax": {
      "Income Tax Filing": [
        "Individual",
        "LLP",
        "Company",
        "AOP",
        "BOI"
      ],
      "TDS & TCS Filing": null,
      "Tax Audit (Form 3CA / 3CB / 3CD)": null,
      "Statutory Audit": null,
      "Pan Correction and Reissue": null,
      "Assessment & Representation": null,
      "Advance Tax and Self Assessment Tax": null,
      "Capital Gains & Investment Advisory": null
    },
    "Indirect Tax": [
      "GST Registrations & Services",
      "GST Return Filing (GSTR 1, 3B, 4, 9/9C)",
      "GST Audit & Certification",
      "GST Refund & Export Related Services",
      "GST Advisory & Planning Tax",
      "Input Tax Credit Management",
      "Representation & Litigation Support",
      "E-invoicing & E-way bill Compliance"
    ]
  },
  "Trademark & Certifications": [
    "Trademark Applications & Filing",
    "TradeMark Search And Advisory",
    "Trademark Renewal",
    "Income / Net worth Certificates",
    "Turn over or Profit Certificates",
    "Statutory Certificates",
    "Provisional/Projected Financial Certificate (Project Report)",
    "Working Capital Certificate",
    "Stock / BookDebt Certificate",
    "Fund Flow Certificate"
  ],
  "MCA Services": {
    "Company Incorporation and Registration": [
      "Private Limited Company Registration",
      "One Person Company Registration (OPC)",
      "Limited Liability Partnership Registration",
      "Section 8 Company (Non Profit Organization)",
      "Public Limited Company"
    ],
    "Post Incorporation Compliances (INC-20A, ADT-1, etc)": null,
    "Conversion of Entity Type (LLP to Pvt ltd, etc)": null,
    "Annual ROC Filings & Compliances": [
      "AOC-4 / AOC-4 XBRL - Filing of Financial Statements",
      "MGT-7 / MGT-7A - Annual Return Filing",
      "ADT-1 - Appointment of Auditor",
      "DIR-3 KYC / DIR-3 KYC Web - Directory KYC",
      "DPT-3 - Return of Deposits / Loans / Advances",
      "MGT-14 - Disclosure of Resolutions"
    ],
    "Director & ShareHolder Services": [
      "DIN Application / Modification",
      "Director Resignation or Appointment Filings",
      "Change in Designation (DIR-12)",
      "DIR-3 KYC filing & Activation",
      "Share Allotment, transfer, and issuance filings",
      "Maintenance of Statutory registers"
    ],
    "Secretarial & Legal Documentation": [
      "Drafting of MOA / AOA Amendments",
      "Drafting of Board Resolutions",
      "Drafting of Shareholder Meeting Notices & Minutes",
      "Drafting of Directors Reports",
      "Drafting of Share Certificates and Registers",
      "Drafting of Share Transfer Forms (SH-4)"
    ]
  },
  "Accounting Services": null
};

export const Chatbot = ({ onFormSubmit }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState("greeting");
  const [navigationPath, setNavigationPath] = useState([]);
  const [currentOptions, setCurrentOptions] = useState(null);
  const [selectedService, setSelectedService] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: ""
  });
  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef(null);
  const hasInitialized = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages.length]);

  useEffect(() => {
    if (isOpen && messages.length === 0 && !hasInitialized.current) {
      hasInitialized.current = true;
      setTimeout(() => {
        addBotMessage("Hi! 👋 What are you looking for?");
        setCurrentStep("selectCategory");
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const addBotMessage = (text) => {
    setMessages(prev => [...prev, { type: "bot", text }]);
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, { type: "user", text }]);
  };

  const getNestedValue = (obj, path) => {
    return path.reduce((current, key) => current?.[key], obj);
  };

  const handleConnectUs = () => {
    addUserMessage("👤 Connect us 📞");
    const servicePath = navigationPath.length > 0 ? navigationPath.join(" > ") : "General Inquiry";
    setSelectedService(servicePath);
    setFormData(prev => ({ ...prev, service: servicePath }));
    setTimeout(() => {
      addBotMessage("Great! Let me collect your details. What's your name?");
      setCurrentStep("askName");
    }, 500);
  };

  const handleOptionSelect = (option) => {
    addUserMessage(option);
    const newPath = [...navigationPath, option];
    setNavigationPath(newPath);
    
    const nextLevel = getNestedValue(servicesHierarchy, newPath);
    
    // Check if this is a leaf node (null, undefined, or no further nesting)
    if (nextLevel === null || nextLevel === undefined) {
      // Leaf node - collect details
      setSelectedService(newPath.join(" > "));
      setFormData(prev => ({ ...prev, service: newPath.join(" > ") }));
      setTimeout(() => {
        addBotMessage("Excellent choice! 👍 To help you better, may I know your name?");
        setCurrentStep("askName");
      }, 500);
    } else if (Array.isArray(nextLevel)) {
      // Array of final options
      setCurrentOptions(nextLevel);
      setTimeout(() => {
        addBotMessage(`Great! Please select from the following options:`);
        setCurrentStep("showOptions");
      }, 500);
    } else if (typeof nextLevel === 'object') {
      // Object with more nested options
      setCurrentOptions(Object.keys(nextLevel));
      setTimeout(() => {
        addBotMessage(`Perfect! Please select from the following ${option} options:`);
        setCurrentStep("showOptions");
      }, 500);
    }
  };

  const handleNameSubmit = () => {
    if (inputValue.trim()) {
      addUserMessage(inputValue);
      setFormData(prev => ({ ...prev, name: inputValue }));
      setInputValue("");
      setTimeout(() => {
        addBotMessage(`Nice to meet you, ${inputValue}! 😊 What's your phone number?`);
        setCurrentStep("askPhone");
      }, 500);
    }
  };

  const handlePhoneSubmit = async () => {
    if (inputValue.trim()) {
      addUserMessage(inputValue);
      const finalData = {
        fullName: formData.name,
        phoneNumber: inputValue,
        email: "",
        service: selectedService,
        source: 'chatbot'
      };
      setFormData({...formData, phone: inputValue});
      setInputValue("");
      
      setTimeout(async () => {
        try {
          // Submit to backend
          const response = await fetch('https://ca-services-pcyc.onrender.com/api/contacts', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(finalData)
          });

          const data = await response.json();

          if (data.success) {
            addBotMessage("Perfect! ✅ Thank you for providing your details. Our team will contact you shortly!");
          } else {
            addBotMessage("✅ Thank you! Your details have been recorded. Our team will contact you shortly!");
          }
        } catch (error) {
          console.error('Error submitting chatbot data:', error);
          addBotMessage("✅ Thank you! Your details have been recorded. Our team will contact you shortly!");
        }

        setCurrentStep("completed");
        
        // Call the parent callback with form data
        if (onFormSubmit) {
          onFormSubmit(finalData);
        }
        
        // Reset after 3 seconds
        setTimeout(() => {
          handleReset();
        }, 3000);
      }, 500);
    }
  };


  const handleInputSubmit = (e) => {
    e.preventDefault();
    if (currentStep === "askName") {
      handleNameSubmit();
    } else if (currentStep === "askPhone") {
      handlePhoneSubmit();
    }
  };

  const handleReset = () => {
    setMessages([]);
    setCurrentStep("greeting");
    setNavigationPath([]);
    setCurrentOptions(null);
    setSelectedService("");
    setFormData({ name: "", phone: "", email: "", service: "" });
    setInputValue("");
    hasInitialized.current = false;
    setIsOpen(false);
  };

  return (
    <>
      {/* Chat Button */}
      <button 
        className={`chat-button ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? '' : '💬'}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <div className="chatbot-header-content">
              <div className="chatbot-avatar">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#2c3e50"/>
                  <path d="M2 17L12 22L22 17" stroke="#2c3e50" strokeWidth="2"/>
                  <path d="M2 12L12 17L22 12" stroke="#2c3e50" strokeWidth="2"/>
                </svg>
              </div>
              <div>
                <div className="chatbot-title">ApkaDostCA Assistant</div>
                <div className="chatbot-status">Online</div>
              </div>
            </div>
            <div className="chatbot-header-actions">
              <button className="chatbot-minimize" onClick={() => setIsOpen(false)}>−</button>
              <button className="chatbot-close" onClick={() => setIsOpen(false)}>✕</button>
            </div>
          </div>

          <div className="chatbot-messages">
            {messages.map((msg, index) => (
              <div key={index} className={`message ${msg.type}`}>
                <div className="message-bubble">{msg.text}</div>
              </div>
            ))}

            {/* Main Category Selection */}
            {currentStep === "selectCategory" && (
              <div className="options-container">
                {Object.keys(servicesHierarchy).map((category, index) => (
                  <button
                    key={index}
                    className="option-button"
                    onClick={() => handleOptionSelect(category)}
                  >
                    {category}
                  </button>
                ))}
                <button
                  className="option-button connect-button"
                  onClick={handleConnectUs}
                >
                  👤 Connect us 📞
                </button>
              </div>
            )}

            {/* Nested Options Selection */}
            {currentStep === "showOptions" && currentOptions && (
              <div className="options-container">
                {currentOptions.map((option, index) => (
                  <button
                    key={index}
                    className="option-button"
                    onClick={() => handleOptionSelect(option)}
                  >
                    {option}
                  </button>
                ))}
                <button
                  className="option-button connect-button"
                  onClick={handleConnectUs}
                >
                  👤 Connect us 📞
                </button>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          {(currentStep === "askName" || currentStep === "askPhone") && (
            <form className="chatbot-input-area" onSubmit={handleInputSubmit}>
              <input
                type={currentStep === "askPhone" ? "tel" : "text"}
                className="chatbot-input"
                placeholder={
                  currentStep === "askName" ? "Enter your name..." :
                  "Enter your phone..."
                }
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                autoFocus
                required
              />
              <button type="submit" className="chatbot-send-button">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
};
